"use client";
import {
  CheckboxComponent,
  LoaderComponent,
  PaperComponent,
  SecureDataComponent,
} from "@/components";
import { PanAadharLinkingModal } from "@/components/Modals/PanAadharLinkingModal";
import { useRouter } from "@/i18n/routing";
import {
  APP_KEYS,
  ConsentType,
  DIGI_KYC_SCHEMA,
  GET_PRODUCT_APPLICATION,
  getConsentPayload,
  IMAGES,
  KYCSTATUS,
  PRODUCT_HANDLER,
  STAGE,
  STAGE_BASED_ROUTING,
  SUBSTAGE,
  WEB_COLORS,
  WEB_EVENTS,
} from "@/lib/constants";
import { useFooter } from "@/lib/context/FooterContext";
import useErrorHandling from "@/lib/hooks/errorhandling";
import { sendEventAnalytics } from "@/lib/hooks/eventAnalytics";
import useStageErrorHandler from "@/lib/hooks/stageErrorHandler";
import {
  useCheckPanLinkiningStatusMutation,
  useConsentCreateMutation,
  useDigikycConsentMutation,
  useGetApplicationQuery,
  useGetCustomerQuery,
  useLazyGetDigiKycRedirectUrlQuery,
  usePanLinkingStatusDataQuery,
} from "@/lib/redux";
import { setModalAttributes } from "@/lib/redux/slice/ModalSlice";
import { Stack, Typography } from "@mui/material";
import dayjs from "dayjs";
import { useFormik } from "formik";
import { useTranslations } from "next-intl";
import { useSearchParams } from "next/navigation";
import { enqueueSnackbar } from "notistack";
import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import AadharKYCCard from "./aadhaar-card";
import FaceVerificationModal from "./face-verification-modal";
import { checkForRpl, checkForRplProductRoute } from "@/lib/utils/rpl.utils";
import { sessionStorageUtil } from "@/lib/utils/common.utils";
import FaceVerificationInstructions from "./FaceVerificationInstructions";
import ROUTES from "@/lib/constants/PageRoutes";

const MAX_ATTEMPTS = 9;
const POLLING_INTERVAL = 20000;

const FaceverificationScreen = ({ product }) => {
  const t = useTranslations("faceverification");
  const router = useRouter();
  const { setActions } = useFooter();
  const params = useSearchParams();
  const statusParam = params.get("status");
  const dispatch = useDispatch();

  const [openPanAadharLinkingModal, setOpenPanAadharLinkingModal] =
    useState(false);
  const [showErrorFaceMatchModal, setShowErrorFaceMatchModal] = useState(false);
  const [showModalContent, setShowModalContent] = useState({
    label: t("usercancelled.label"),
    subLabel: t("usercancelled.subLabel"),
    buttonLabel: t("usercancelled.buttonLabel"),
    icon: IMAGES.faceNotVerified,
  });
  const [addressConsent, setAddressConsent] = useState(null);
  const [pollTime, setPollTime] = useState(0);
  const [polling, setPolling] = useState(false);
  const [attemptCount, setAttemptCount] = useState(0);
  const [retryReqd, setRetryReqd] = useState(false);

  const { data: customerData, isLoading: fetchingCustomer } =
    useGetCustomerQuery();
  // fetch application data from get api
  const {
    data: applicationData,
    error: errorGetApplication,
    isFetching: applicationLoading,
    isSuccess: fetchingApplicationSuccess,
    refetch: refetchApplicationData,
  } = useGetApplicationQuery("", {
    refetchOnMountOrArgChange: true,
    pollingInterval: pollTime,
  });

  const productApplication = GET_PRODUCT_APPLICATION(
    PRODUCT_HANDLER[product],
    applicationData?.data,
  );

  const {
    data: panLinkStatusData,
    isFetching: isFetchingPanLinkStatus,
    isError: isErrorPanLinkingStatus,
  } = usePanLinkingStatusDataQuery(productApplication?.appId, {
    skip:
      !productApplication?.appId ||
      productApplication?.stage !== STAGE.PAN_AADHAAR_LINK_PENDING,
  });
  const [
    checkPanLinkingStatus,
    {
      data: panLinkingStatusData,
      isLoading: isLoadingPanLinkingStatus,
      isError: isErrorCheckingPanLinkingStatus,
      isSuccess: isSuccessCheckingPanLinkingStatus,
    },
  ] = useCheckPanLinkiningStatusMutation();
  const [
    digikycConsent,
    {
      data: digikycConsentData,
      error: errorDigikycConsent,
      isLoading: digikycConsentLoading,
    },
  ] = useDigikycConsentMutation();

  const [
    createConsent,
    {
      data: consentData,
      isLoading: consentDataLoading,
      error: errorCreateConsent,
      isSuccess: consentCreated,
    },
  ] = useConsentCreateMutation();

  const [getDigiKycRedirectUrl, redirectUrl] =
    useLazyGetDigiKycRedirectUrlQuery();

  const isError =
    errorGetApplication ||
    isErrorPanLinkingStatus ||
    isErrorCheckingPanLinkingStatus ||
    errorDigikycConsent;
  const isLoading =
    applicationLoading ||
    fetchingCustomer ||
    isFetchingPanLinkStatus ||
    isLoadingPanLinkingStatus ||
    polling ||
    digikycConsentLoading || consentDataLoading;

  /** --------------------------EFFECT-------------------------- **/

  //FORMIK
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      consent: false,
    },
    validationSchema: DIGI_KYC_SCHEMA,
    onSubmit: async (values) => {
      const consentPayload = getConsentPayload(
        ConsentType.DIGI_KYC,
        productApplication,
        Boolean(formik?.values?.consent),
      );
      await createConsent(consentPayload)?.unwrap();
      if (values.consent) {
        getDigiKycRedirectUrl({ appId: productApplication?.appId });
      }
    },
  });
  useEffect(() => {
    if (redirectUrl?.data?.data?.redirectUrl) {
      product && sessionStorage.setItem(APP_KEYS.PRODUCT_TYPE, product);
      window.location.href = redirectUrl?.data?.data?.redirectUrl;
    }
  }, [redirectUrl?.data, product]);

  const shouldRedirect = (stage) =>
    stage &&
    stage !== STAGE.DIGI_KYC &&
    stage !== STAGE.PAN_AADHAAR_LINK_PENDING &&
    stage !== STAGE.CREDIT_QUEUE;

  //dispaly errors
  useEffect(() => {
    if (shouldRedirect(productApplication?.stage)) {
      if (productApplication?.stage == STAGE?.E_SIGN) {
        router.push(ROUTES(product).E_SIGN_INSTRUCTIONS);
        return;
      }
      router.push(STAGE_BASED_ROUTING(productApplication?.stage));
    }
  }, [productApplication?.stage, product, statusParam, params]);

  useErrorHandling(isError);
  useStageErrorHandler({
    stage: productApplication?.stage,
    prevStage: productApplication?.previousStage,
    message: productApplication?.loanReapplyMessage,
  });
  useEffect(() => {
    refetchApplicationData();
    const isRPL = checkForRplProductRoute(product);
    const redirectAfterDigikyc= params?.size > 0;  // has search params implies redirected, hence don't trigger it again;
    const correctProductType = sessionStorageUtil.get(APP_KEYS.PRODUCT_TYPE) === product;
    const shouldSendEvent = redirectAfterDigikyc ? correctProductType : true; // if redirected, send event only if product type is correct;

    sendEventAnalytics({
      eventName: isRPL ? WEB_EVENTS.RL_ON_KYC_SCREEN : WEB_EVENTS.ON_KYC_VERIFICATION_SCREEN,
      skip: !shouldSendEvent,
      ...productApplication,
      ...customerData?.data,
    });
  }, [params]);

  useEffect(() => {
    if (shouldRedirect(productApplication?.stage)) return;

    const closePolling = () => {
      if (polling) {
        setPolling(false);
        setPollTime(0);
      }
    };

    const shouldPoll = () => {
      const { digiKycStatus, subStage, stage } = productApplication || {};
      if (
        stage === STAGE.CREDIT_QUEUE ||
        digiKycStatus === KYCSTATUS.CREDIT_QUEUE
      )
        return false;
      if (attemptCount === MAX_ATTEMPTS) return false;
      if (
        [
          SUBSTAGE.PAN_DOB_MISMATCH,
          SUBSTAGE.PAN_AADHAR_MISMATCH,
          SUBSTAGE.DIGI_KYC_FAIL_FACEMATCH,
          SUBSTAGE.PAN_KYC_NAME_MATCH_CREDIT_QUEUE,
          SUBSTAGE.REJECT,
          SUBSTAGE.DIGI_KYC_FAIL,
        ].includes(subStage)
      ) {
        return false;
      }
      if (digiKycStatus === SUBSTAGE.DIGI_KYC_INITIATED) return true;
      return true;
    };


    if (
      (statusParam === "auto_approved" || statusParam === "auto_declined") &&
      !polling && shouldPoll()
    ) {
      setPolling(true);
      setPollTime(POLLING_INTERVAL);
    }
    if (statusParam === "user_cancelled") {
      setShowErrorFaceMatchModal(true);
      setShowModalContent({
        label: t("usercancelled.label"),
        subLabel: t("usercancelled.subLabel"),
        buttonLabel: t("usercancelled.buttonLabel"),
        icon: IMAGES.faceNotVerified,
      });
      return;
    }

    if (attemptCount === MAX_ATTEMPTS) {
      closePolling();
      enqueueSnackbar("Failed to verify identity. Please try again.", {
        variant: "error",
        autoHideDuration: 3000,
        preventDuplicate: true,
      });
      return;
    }
    const { subStage, stage, loanApplyDate, digiKycStatus } =
      productApplication || {};
    if (stage === STAGE.OFFERS && subStage === "PAN_DOB_MISMATCH") {
      router.push(`/${product}/offers`);
      return;
    }

    if (stage === KYCSTATUS.CREDIT_QUEUE) {
      closePolling();
      dispatch(
        setModalAttributes({
          open: true,
          type: KYCSTATUS.CREDIT_QUEUE,
        }),
      );  
      return;
    }
    if (digiKycStatus === SUBSTAGE.DIGI_KYC_INITIATED) {
      setTimeout(() => {
        setRetryReqd(true);
      }, 60000);
    }
    if (digiKycStatus === STAGE.CREDIT_QUEUE) {
      closePolling();
      dispatch(
        setModalAttributes({
          open: true,
          message: "Your selfie couldn’t be verified. Please try again.",
          type: "FACE_MISMATCH_ERROR",
        }),
      );
      return;
    }
    if (subStage === SUBSTAGE.DIGI_KYC_FAIL) {
      closePolling();
      dispatch(
        setModalAttributes({
          open: true,
          message: productApplication?.loanReapplyMessage,
          type: SUBSTAGE.DIGI_KYC_FAIL,
        }),
      );
      return;
    }
    if (
      [SUBSTAGE.PAN_DOB_MISMATCH, SUBSTAGE.PAN_AADHAR_MISMATCH].includes(
        subStage,
      )
    ) {
      refetchApplicationData();
      setShowErrorFaceMatchModal(true);
      setShowModalContent({
        label: loanApplyDate
          ? `${t("loanApplyDate.label1")} ${loanApplyDate}`
          : t("loanApplyDate.label2"),
        subLabel: loanApplyDate ? "" : t("loanApplyDate.subLabel"),
        buttonLabel: t("loanApplyDate.buttonLabel"),
        icon: IMAGES.faceNotVerified,
      });
      return;
    }          
    if (
      subStage === KYCSTATUS?.PAN_KYC_NAME_MATCH_CREDIT_QUEUE ||
      subStage === SUBSTAGE?.REJECT ||
      subStage === SUBSTAGE?.DIGI_KYC_FAIL_FACEMATCH
    ) {
      if (subStage === KYCSTATUS?.PAN_KYC_NAME_MATCH_CREDIT_QUEUE) {
        dispatch(
          setModalAttributes({
            open: true,
            message:
              "Looks like, we would require to screen your application a little more. We will get back to you in next 24 hours",
            type: KYCSTATUS?.PAN_KYC_NAME_MATCH_CREDIT_QUEUE,
          }),
        );
      } else {
        dispatch(
          setModalAttributes({
            open: true,
            message:
              "We couldn’t complete your face verification. Your application is now under manual review and will be updated within 24 hours",
            type: SUBSTAGE?.DIGI_KYC_FAIL_FACEMATCH,
          }),
        );
      }
      closePolling();
      return;
    }
    if (
      subStage === KYCSTATUS.STP ||
      subStage === SUBSTAGE.UCIC_HUNTER_DECISION_POSTPONED_REJECT
    ) {
      refetchApplicationData();
      return;
    }
    if (stage === STAGE.WORK_DETAILS) {
      router.push(STAGE_BASED_ROUTING(productApplication?.stage));
    }
    // Polling is now handled by RTK Query pollingInterval, not manual refetch
  }, [productApplication, statusParam, pollTime, polling, attemptCount, product, params]);
  useEffect(() => {
    setActions({
      showConfirm: true,
      showReject: true,
      disabledConfirmBtn:
        productApplication?.digiKycStatus === SUBSTAGE.DIGI_KYC_INITIATED
          ? false
          : !(formik.dirty && formik.isValid && addressConsent),
      confirmBtnLabel:
        (statusParam === "auto_approved" || statusParam === "auto_declined") &&
        productApplication?.digiKycStatus !== SUBSTAGE.CREDIT_QUEUE
          ? "Refetch Status"
          : "Proceed To Aadhaar KYC",
      onConfirm:
        (statusParam === "auto_approved" || statusParam === "auto_declined") &&
        productApplication?.digiKycStatus !== SUBSTAGE.CREDIT_QUEUE
          ? () => {
              setAttemptCount(0);
              setPolling(true);
              setPollTime(POLLING_INTERVAL);
            }
          : formik.handleSubmit,
    });
  }, [
    setActions,
    addressConsent,
    formik.values.consent,
    formik.isValid,
    formik.dirty,
    productApplication?.digiKycStatus,
  ]);
  useEffect(() => {
    if (
      isSuccessCheckingPanLinkingStatus &&
      panLinkingStatusData?.data?.stage === STAGE.PAN_AADHAAR_LINK_PENDING
    ) {
      enqueueSnackbar("Pan Aadhaar is not Linked, Please try Again", {
        variant: "error",
        autoHideDuration: 3000,
      });
    } else {
      refetchApplicationData();
    }
  }, [isSuccessCheckingPanLinkingStatus]);
  /** --------------------------FUNCTIONS-------------------------- **/

  const handleFaceVerificationError = () => {
    setShowErrorFaceMatchModal(false);
    refetchApplicationData();
  };

  useEffect(() => {
    if (!polling) return;
    if (applicationLoading && fetchingApplicationSuccess) {
      setTimeout(() => {
        setAttemptCount((count) => count + 1);
      }, POLLING_INTERVAL);
    }
  }, [applicationLoading, fetchingApplicationSuccess, polling]);

  useEffect(() => {
    if (productApplication?.stage === STAGE.PAN_AADHAAR_LINK_PENDING) {
      setOpenPanAadharLinkingModal(true);
    }
  }, [productApplication?.stage]);

  /** --------------------------RENDER-------------------------- **/
  return (
    <PaperComponent>
      <LoaderComponent open={isLoading} />
      <FaceVerificationInstructions
        addressConsent={addressConsent}
        handleAddressConsent={(e) => {
          setAddressConsent(e.target.checked);
          digikycConsent({
            appId: productApplication?.appId,
            addressConsent: e.target.checked,
            consentType: ConsentType.DIGI_KYC,
            consentValue: { consent: e.target.checked },
          });
        }}
        handleKycConsent={formik.handleChange}
        kycConsent={formik.values.consent}
      />
      <FaceVerificationModal
        data={showModalContent}
        open={showErrorFaceMatchModal}
        closeModal={() => setShowErrorFaceMatchModal(false)}
        handleModal={handleFaceVerificationError}
      />
      <PanAadharLinkingModal
        panAadhaarHaltedAt={panLinkStatusData?.data?.panAadhaarHaltedAt}
        attemptsLeft={
          panLinkStatusData?.data?.panAadhaarVerifyAttemptsRemaining
        }
        open={openPanAadharLinkingModal}
        dayLimit={panLinkStatusData?.data?.panAadhaarVerificationDaysRemaining}
        showBtn={
          dayjs().diff(
            dayjs(panLinkStatusData?.data?.panAadhaarHaltedAt),
            "day",
          ) >= 1 &&
          panLinkStatusData?.data?.panAadhaarVerifyAttemptsRemaining > 0
        }
        btnOnClick={() =>
          checkPanLinkingStatus({ appId: productApplication?.appId })
        }
      />
    </PaperComponent>
  );
};

export default FaceverificationScreen;
